import { useState } from "react";
import { useNavigate } from "react-router";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Link } from "@/components/ui/link";
import { authClient, useSession } from "@/lib/auth-client";
import { useMarkAppBootstrapSignedOut } from "@/lib/hooks/use-app-bootstrap";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./user-menu.i18n";

type UserMenuProps = {
  user: {
    name?: string | null;
    email?: string | null;
    image?: string | null;
    tenantSlug: string;
  };
};

export function UserMenu({ user }: UserMenuProps) {
  const navigate = useNavigate();
  const markBootstrapSignedOut = useMarkAppBootstrapSignedOut();
  const t = useT(T);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const { data: session } = useSession();

  // Use session image (reactive) with fallback to prop
  const image = session?.user?.image ?? user.image;

  const handleSignOut = async () => {
    setIsLoggingOut(true);
    await authClient.signOut();
    // QL-628 — never serve this user's bootstrap to a later session in this tab.
    markBootstrapSignedOut();
    navigate("/login");
  };

  const initials = user.name
    ? user.name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2)
    : user.email?.slice(0, 2).toUpperCase() || "U";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="relative h-8 w-8 rounded-full">
          <Avatar className="h-8 w-8">
            <AvatarImage src={image || undefined} alt={user.name || t.user} />
            <AvatarFallback>{initials}</AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium">{user.name || t.user}</p>
            <p className="text-xs text-muted-foreground">{user.email}</p>
            <p className="text-xs text-muted-foreground">
              {t.org}: {user.tenantSlug}
            </p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <Link to="/settings/profile">
          <DropdownMenuItem className="cursor-pointer">{t.profile}</DropdownMenuItem>
        </Link>
        <Link to="/settings/notifications">
          <DropdownMenuItem className="cursor-pointer">{t.notifications}</DropdownMenuItem>
        </Link>
        <Link to="/settings/security">
          <DropdownMenuItem className="cursor-pointer">{t.security}</DropdownMenuItem>
        </Link>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleSignOut} disabled={isLoggingOut}>
          {isLoggingOut ? t.loggingOut : t.logOut}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
